import type { Rarity } from './rarity';

/**
 * Wave L-2 (PLAN-CITY2 §6): the 9 cosmetic wearables Codex delivered as C09
 * (`city\briefs\C09-cosmetic-wearables.md`), independently re-validated
 * (`tools/validate-glb.mjs` 9/9 PASS) before this registry was written.
 * Slot/rarity/source values here are this session's own Wave L-1 design
 * calls (the brief's own rarity column is explicitly "indicative only, final
 * tiers are Wave L-1 game data, not [Codex's] to encode" — see the brief's
 * "The pieces" table header), following its §6 ladder note: cosmetics are
 * EPIC/LEGENDARY only. Ids match the delivered filenames exactly
 * (`prp_wear_<piece>.glb`, minus the extension) — `economy/drops.ts` and
 * `world/playerVisual.ts` both key off these ids to load
 * `/assets/models/<id>.glb` via the same `core/assets.ts` loader every other
 * entity uses.
 */
export type WearableSlot = 'head' | 'torso' | 'back';

export type WearableId =
  | 'prp_wear_rat_crown'
  | 'prp_wear_signal_crest'
  | 'prp_wear_gold_plume'
  | 'prp_wear_manhole_plate'
  | 'prp_wear_rat_ruff'
  | 'prp_wear_rat_cape'
  | 'prp_wear_hubcap_pack'
  | 'prp_wear_trash_lid'
  | 'prp_wear_pizza_slice';

export interface WearableDef {
  id: WearableId;
  slot: WearableSlot;
  label: string;
  rarity: Rarity;
  /** "How did you get that?" flex text — Karan's own G2 mandate for Loot 2.0's top tier. Shown in the wardrobe UI and the rare-drop toast (L-4). */
  sourceLabel: string;
}

export const WEARABLE_ORDER: readonly WearableId[] = [
  'prp_wear_rat_crown',
  'prp_wear_signal_crest',
  'prp_wear_gold_plume',
  'prp_wear_manhole_plate',
  'prp_wear_rat_ruff',
  'prp_wear_rat_cape',
  'prp_wear_hubcap_pack',
  'prp_wear_trash_lid',
  'prp_wear_pizza_slice',
];

export const WEARABLES: Record<WearableId, WearableDef> = {
  prp_wear_rat_crown: {
    id: 'prp_wear_rat_crown',
    slot: 'head',
    label: "Rat King's Crown",
    rarity: 'legendary',
    sourceLabel: 'The Rat King — first kill',
  },
  prp_wear_signal_crest: {
    id: 'prp_wear_signal_crest',
    slot: 'head',
    label: 'Signal Crest',
    rarity: 'epic',
    sourceLabel: 'Gridlock Golem',
  },
  prp_wear_gold_plume: {
    id: 'prp_wear_gold_plume',
    slot: 'head',
    label: 'Golden Plume',
    rarity: 'legendary',
    sourceLabel: 'The Golden Pigeon',
  },
  prp_wear_manhole_plate: {
    id: 'prp_wear_manhole_plate',
    slot: 'torso',
    label: 'Manhole Plate',
    rarity: 'epic',
    sourceLabel: 'Sewer Elite',
  },
  prp_wear_rat_ruff: {
    id: 'prp_wear_rat_ruff',
    slot: 'torso',
    label: 'Ratty Ruff',
    rarity: 'epic',
    sourceLabel: 'Street Rats',
  },
  prp_wear_rat_cape: {
    id: 'prp_wear_rat_cape',
    slot: 'back',
    label: "Rat King's Cape",
    rarity: 'epic',
    sourceLabel: 'The Rat King — repeat kill',
  },
  prp_wear_hubcap_pack: {
    id: 'prp_wear_hubcap_pack',
    slot: 'back',
    label: 'Hubcap Pack',
    rarity: 'epic',
    sourceLabel: 'Gridlock Golem',
  },
  prp_wear_trash_lid: {
    id: 'prp_wear_trash_lid',
    slot: 'back',
    label: 'Trash Lid Buckler',
    rarity: 'epic',
    sourceLabel: 'Trash-Can Lobber',
  },
  prp_wear_pizza_slice: {
    id: 'prp_wear_pizza_slice',
    slot: 'back',
    label: 'Pizza Slice',
    rarity: 'epic',
    sourceLabel: 'Pizza Rat',
  },
};

export function isWearableId(v: string): v is WearableId {
  return Object.prototype.hasOwnProperty.call(WEARABLES, v);
}
